import { gql } from "@apollo/client";
import { faCalculator, faPlus, faRefresh, faSave, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import DateRangeInput from "components/common/form/inputs/date/DateRangeInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import HTMLCode from "components/common/HTMLCode";
import Translate from "components/common/Translate";
import { api, graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { DropdownInputPropsType } from "types/form-types";
import { CheckAvailabilityQuery, CheckAvailabilityQueryVariables, SortEnumType } from "types/graphql";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import RoomTypeView from "../roomType/RoomTypeView";
import AvailabilityTable, { AvailabilityRoomType, SelectData } from "./AvailabilityTable";
import * as lodash from 'lodash';
import './CheckAvailability.scss';
import BookingView from "../booking/views/BookingView";
import AppLink from "components/common/AppLink";
import { AppRoutes } from "config/router";
import Input from "components/common/form/inputs/Input";
import If from "components/common/If";
import AvailableRoomTypeCountView from "./AvailableRoomTypeCountView";
import { Roles } from "utilities/app-utilities";
import DateInput from "../../../common/form/inputs/date/DateInput";

type CheckAvailabilityPropsType = {
    dates?: {
        start: Date;
        end: Date;
    }
    name: string
    readonly?: boolean
}

const query = gql`
query CheckAvailability(
    $where: BookingDetailsFilterInput
    $sort: [BookingDetailsSortInput!]
    $roomSort: [RoomSortInput!]
    $roomWhere: RoomFilterInput
  ) {
    rooms {
      items(where: $roomWhere, order: $roomSort) {
        roomType {
          color
          id
          name
          defaultPrice
        }
        name
        id
        order
        bookingDetails {
          items(where: $where, order: $sort) {
            price
            booking {
              inDate
              outDate
              inSeasonId
              id
              nights
              roomType {
                id
                name
                color
              }
            }
            date
            id
          }
          count (where: $where)
        }
      }
    }
    fistDate: bookingDetails(order: [{date: ASC}], where: $where)  {
      date
    }
    lastDate: bookingDetails(order: [{date: DESC}], where: $where) {
      date
    }
  }  
`;


const CheckAvailability = (props: CheckAvailabilityPropsType) => {
    const context = useAppContext();
    const [dates, setDates] = useState<Date[]>(props.dates ? DateUtil.addMissingDates([props.dates.start, props.dates.end])! : []);
    const [selectedRoomType, setSelectedRoomType] = useState<AvailabilityRoomType['roomType']>();
    const [selectedDates, setSelectedDates] = useState<Date[]>(props.dates ? DateUtil.addMissingDates([props.dates.start, props.dates.end])! : []);
    const [selectedDateToShow, setSelectedDateToShow] = useState<Date>(new Date());
    const [selectedBookingId, setSelectedBookingId] = useState<number>(0);
    const [data, setData] = useState<AvailabilityRoomType[]>([]);
    const [componentState, setComponentState] = useState<ComponentStateTypes>("loading");
    const [price, setPrice] = useState<number | string>('');
    const [amount, setAmount] = useState<number | string>(1);

    const roomTypeDropdownConfig: DropdownInputPropsType<AvailabilityRoomType['roomType']> = {
        label: 'fields.roomType.name',
        placeholder: `${context.t('common.select')} ${context.t('fields.roomType.name').toLowerCase()}....`,
        getItemId: (item) => item.id,
        dataSource: {
            data: () => lodash.uniqBy(data.map(d => d.roomType), i => i.id),
            search: "off"
        },
        onChange: i => setSelectedRoomType(i),
        value: selectedRoomType,
        view: {
            inputView: i => i?.name,
            dropdownView: i => <RoomTypeView {...i} />
        },
        name: 'roomType'
    }

    // #region load data
    useEffect(() => {
        setSelectedDates(props.dates ? DateUtil.addMissingDates([props.dates.start, props.dates.end])! : []);
        loadData().then();
    }, [props]);

    useEffect(() => {
        loadData().then();
    }, [selectedDateToShow]);

    const loadData = async () => {
        setComponentState("loading");
        try {
            const response = await graph<CheckAvailabilityQuery, CheckAvailabilityQueryVariables>({
                query,
                variables: {
                    where: {
                        and: [
                            {
                                booking: {
                                    season: {
                                        active: {
                                            eq: true
                                        }
                                    }
                                }
                            },
                            {
                                booking: {
                                    outDate: {
                                        gte: DateUtil.toIsoDate(selectedDateToShow)
                                    }
                                }
                            },
                            {
                                date: {
                                    gte: DateUtil.toIsoDate(selectedDateToShow)
                                }
                            },
                            {
                                booking: {
                                    isCancelled: {
                                        "eq": false
                                    }
                                }
                            }
                        ]
                    },
                    sort: [
                        {
                            date: SortEnumType.Asc
                        }
                    ],
                    roomSort: {
                        name: SortEnumType.Asc
                    }
                }
            });

            if (response.errors) {
                throw response.errors;
            } else if (!response.data?.rooms?.items.length) {
                setComponentState('nodata');
                setDates([]);
            } else {
                const availabilityRooms: AvailabilityRoomType[] = response.data!.rooms!.items.map(r => {
                    return {
                        id: r.id,
                        name: r.name,
                        order: r.order || undefined,
                        roomType: r.roomType,
                        bookingDetails: r.bookingDetails.items.map(b => {
                            return {
                                booking: b.booking,
                                date: new Date(b.date),
                                id: b.id,
                                price: b.price,
                                roomId: r.id
                            }
                        })
                    }
                });
                setData(availabilityRooms);
                setComponentState("");
                setDates(DateUtil.addMissingDates([response.data.fistDate?.date || new Date(), response.data.lastDate?.date || new Date()])!);
            }
        } catch (error: any) {
            context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            setComponentState("error");
            throw error;
        }
    }
    // #endregion load

    const onDateSelect = (dateFilter: Date[]) => {
        setSelectedDates(dateFilter);
        if (dateFilter.length) {
            const date = DateUtil.format(dateFilter[0], DateFormats.ISO);
            const offset = (document.querySelector(`td[data-date='${date}']`) as any)?.offsetLeft;
            document.querySelector('.check-available')?.scroll({
                left: (offset - 80) || 0,
                behavior: 'smooth'
            });
        }
    }

    // #region move data
    const onSelect = (e: React.MouseEvent, selectData?: SelectData) => {
        let changeData = [...data];
        changeData.forEach(r => {
            r.bookingDetails.forEach(b => {
                if (b.booking.id === selectData?.bookingDetail.booking.id && b.isSelected) {

                }
                else if (b.booking.id === selectData?.bookingDetail.booking.id) {
                    b.isSelected = e.shiftKey || (selectData.bookingDetail.id === b.id);
                }
                else {
                    b.isSelected = false;
                }
            });
        });
        setData(changeData);
    }
    const onChangeData = (roomId: number) => {
        const dataToEdit = lodash.cloneDeep(data);
        for (let index = 0; index < data.length; index++) {
            const room = data[index];
            if (room.id === Number(roomId)) {
                dataToEdit.forEach(r => {
                    const bookingDetail = r.bookingDetails.filter(b => b.isSelected && b.roomId !== room.id);
                    bookingDetail.forEach(b => {
                        room.bookingDetails.push({
                            ...b,
                            roomId: room.id,
                            changed: true,
                        });
                    });
                });
            } else {
                room.bookingDetails = room.bookingDetails.filter(b => !b.isSelected);
            }
        }
    }

    const onSave = async () => {
        const currentSelectedBooking = Number(selectedBookingId);
        setSelectedBookingId(0);
        const saveData = data.map(r => {
            return r.bookingDetails.filter(b => b.changed).map(b => {
                return {
                    id: b.id,
                    roomId: b.roomId,
                    price: b.price,
                }
            });
        }).flat();
        if (!saveData?.length) {
            context.notify('info', 'request.save.nothing-to-save');
            return;
        }

        try {
            await api.put('/bookingDetails/UpdateRooms', saveData);
            context.notify('success', 'request.save.success');
            await loadData();
            if (currentSelectedBooking) setSelectedBookingId(currentSelectedBooking);
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        }
    }
    // #endregion move data

    const onAdd = () => {
        const bookingModel = {
            inDate: selectedDates.length && selectedDates[0],
            outDate: selectedDates.length && selectedDates[selectedDates.length - 1],
            roomTypeId: selectedRoomType?.id,
            pricePerNight: price,
        }

        let queryParams = new URLSearchParams();
        queryParams.append('inDate', bookingModel.inDate ? DateUtil.toIsoDate(bookingModel.inDate) : '');
        queryParams.append('outDate', bookingModel.outDate ? DateUtil.toIsoDate(bookingModel.outDate) : '');
        queryParams.append('roomTypeId', bookingModel.roomTypeId?.toString() || '');
        queryParams.append('pricePerNight', bookingModel.pricePerNight ? bookingModel.pricePerNight.toString() : '');
        let url = AppRoutes.ACCOMMODATION_BOOKING_FORM + '?' + queryParams.toString();
        context.navigate(url);
    };

    return (
        <div key={`${props.name}-check-ability`}>
            <If condition={!props.readonly}>
                <div>
                    <DateInput
                        label={context.t("accommodation.availability.selectDatesToShow")}
                        name="dates"
                        placeholder={context.t("accommodation.availability.toDayTillTheLastReservation")}
                        value={selectedDateToShow}
                        max={dates ? dates[dates.length - 1] : undefined}
                        onChange={v => setSelectedDateToShow(v || new Date())}
                    />
                </div>
                <div>
                    <DateRangeInput
                        label={context.t("accommodation.availability.selectDates")}
                        calculateAsNights
                        name="dates"
                        format={DateFormats.SHORT}
                        placeholder={`${context.t('common.select')} ${context.t('fields.dates.name').toLowerCase()}....`}
                        value={selectedDates}
                        min={dates ? dates[0] : undefined}
                        max={dates ? dates[dates.length - 1] : undefined}
                        onChange={v => onDateSelect(v || [])}
                        description={<span><Translate i18nKey='accommodation.booking.totalNights' />: {(selectedDates?.length || 1) - 1}</span>}
                    />
                </div>
                <div>
                    <DropdownInput
                        {...roomTypeDropdownConfig}
                        value={selectedRoomType}
                    />
                </div>
                <If condition={selectedDates?.length}>
                    <div className="grid grid-cols-3">
                        <Input
                            value={price?.toString()}
                            onChange={v => setPrice(v || '')}
                            type="number"
                            label="fields.pricePerNight.name"
                            suffix={{
                                icon: faCalculator,
                                name: 'setDefaultPrice',
                                onClick: () => selectedRoomType?.defaultPrice && setPrice(selectedRoomType!.defaultPrice!.toString() || ''),
                            }}
                        />
                        <Input
                            value={amount?.toString()}
                            onChange={v => setAmount(v || '')}
                            label="accommodation.availability.roomCount"
                            type="number"
                        />
                        <Input
                            value={(price && selectedDates ? (Number(price) * (Number(amount) || 1) * (selectedDates.length - 1)).toFixed(2) : 0).toString()}
                            readonly
                            label="fields.total.name"
                        />
                    </div>
                    <div className="pb-2">
                        <AvailableRoomTypeCountView
                            data={data}
                            selectedDates={selectedDates?.length ? selectedDates.slice(0, -1) : []}
                            roomCount={Number(amount) || 1}
                        />
                    </div>
                    <div>
                        <Button theme="primary" title="button.add" type="button" onClick={onAdd}>
                            <FontAwesomeIcon icon={faPlus} />
                            <HTMLCode />
                            <Translate i18nKey="button.add" />
                            <HTMLCode />
                            <span className="lowercase">
                                <Translate i18nKey="accommodation.booking.name" />
                            </span>
                        </Button>
                    </div>
                </If>
                <div className="w-full py-2 text-right">
                    <Button className="my-auto" title="button.reload" onClick={loadData}>
                        <><FontAwesomeIcon icon={faRefresh} /> <span>{context.t('button.reload')}</span></>
                    </Button>
                </div>
                <hr />
            </If>
            <ComponentState state={componentState}>
                <div className="check-available">
                    <AvailabilityTable
                        selectedDates={selectedDates?.length ? selectedDates.slice(0, -1) : []}
                        selectedRoomTypeId={selectedRoomType?.id}
                        onChange={onChangeData}
                        dates={dates} data={data}
                        onSelect={onSelect}
                        view={(_, v) => setSelectedBookingId(v.bookingDetail.booking.id || 0)}
                    />
                </div>
                <div className="text-center p-5">
                    <If condition={context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE) && !props.readonly}>
                        <Button theme="primary" title="button.save" type="button" onClick={() => onSave().then()}>
                            <FontAwesomeIcon icon={faSave} />
                            <HTMLCode />
                            <Translate i18nKey="button.save" />
                        </Button>
                    </If>
                </div>
            </ComponentState>
            {selectedBookingId > 0 && !props.readonly ? <>
                <div>
                    <header>
                        <span className="float-right">
                            <AppLink to={AppRoutes.ACCOMMODATION_BOOKING_VIEW} params={{ id: selectedBookingId }}>
                                <FontAwesomeIcon icon={faUpRightFromSquare} />
                            </AppLink>
                        </span>
                        <h1>{context.t('pages.accommodation.booking.view')} {selectedBookingId}</h1>
                    </header>
                    <hr />
                    <BookingView name={props.name + "check-ava-booking-view"} bookingId={selectedBookingId} />
                </div>
            </> : <></>}
        </div>
    );
};

export default CheckAvailability;
